
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import InputForm from './components/InputForm';
import FloorPlanCanvas from './components/FloorPlanCanvas';
import HelpModal from './components/HelpModal';
import AlertDisplay from './components/AlertDisplay';
import GuestListModal from './components/GuestListModal'; 
import { 
  Theme, VenueConfig, TableConfig, DanceFloorConfig, FormData, TableShape, 
  MainTableFormData, ItemOrientation, CustomElementFormData, CustomElementConfig
} from './types';
import { 
  DEFAULT_VENUE_WIDTH, DEFAULT_VENUE_LENGTH, DEFAULT_GUESTS, DEFAULT_SEATS_PER_TABLE,
  DEFAULT_MAIN_TABLE_WIDTH, DEFAULT_MAIN_TABLE_LENGTH, DEFAULT_MAIN_TABLE_SEATS,
  DANCE_FLOOR_DIMENSIONS_BY_GUESTS,
  DEFAULT_CUSTOM_ELEMENT_WIDTH,
  DEFAULT_CUSTOM_ELEMENT_HEIGHT
} from './constants';
import { generatePdf } from './services/pdfService';

// --- Constantes Iniciales (Fuera del componente para referencia estática) ---
const INITIAL_MAIN_TABLE_DATA: MainTableFormData = {
    width: DEFAULT_MAIN_TABLE_WIDTH.toString(),
    length: DEFAULT_MAIN_TABLE_LENGTH.toString(),
    seats: DEFAULT_MAIN_TABLE_SEATS.toString(),
};

const INITIAL_FORM_DATA: FormData = {
    venueWidth: DEFAULT_VENUE_WIDTH.toString(),
    venueLength: DEFAULT_VENUE_LENGTH.toString(),
    guests: DEFAULT_GUESTS.toString(),
    tableShape: 'round',
    seatsPerStandardTable: DEFAULT_SEATS_PER_TABLE,
    danceFloorEnabled: false,
    danceFloorCustomWidth: '', 
    danceFloorCustomHeight: '', 
    enableMainTable: false,
    mainTableData: INITIAL_MAIN_TABLE_DATA,
    customElements: [], 
};


// --- Utiles de Geometría y Física ---

const getSuggestedDanceFloorDims = (numGuests: number) => {
    const dim = DANCE_FLOOR_DIMENSIONS_BY_GUESTS.find(d => numGuests >= d.minGuests && numGuests <= d.maxGuests) || 
                DANCE_FLOOR_DIMENSIONS_BY_GUESTS[DANCE_FLOOR_DIMENSIONS_BY_GUESTS.length - 1];
    return { width: dim.width, height: dim.height };
};

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Configuraciones físicas
const CHAIR_DEPTH = 0.5; // Espacio que ocupa una silla hacia afuera de la mesa

// Buffer para Movimiento Manual: 0.5m
// Permite que las mesas se acerquen hasta 50cm entre bordes físicos (sillas entrelazadas).
const MANUAL_TABLE_BUFFER = 0.5;

// Buffer para Generación Automática: 1.5m (Silla 0.5 + Paso 0.5 + Silla 0.5)
// Garantiza circulación de mozos en el diseño inicial sugerido.
const AUTO_TABLE_BUFFER = 1.5;

// Buffer para Mesa vs Pista/Obstaculo = (Silla + Margen mínimo) = 0.6m
const TABLE_TO_OBSTACLE_BUFFER = CHAIR_DEPTH + 0.1;

const getPhysicalDimensions = (shape: TableShape, seats: number): { width: number, height: number } => {
    if (shape === 'round') {
        let diameter = 1.5; // Default 8 seats
        if (seats <= 4) diameter = 1.0;
        else if (seats <= 6) diameter = 1.2;
        else if (seats <= 8) diameter = 1.5;
        else if (seats <= 10) diameter = 1.8;
        else diameter = 2.1;
        return { width: diameter, height: diameter };
    } else if (shape === 'rectangular') {
        let w = 2.4; let h = 0.9;
        if (seats <= 4) { w = 1.2; h = 0.75; }
        else if (seats <= 6) { w = 1.8; h = 0.8; }
        else if (seats <= 8) { w = 2.4; h = 0.9; }
        else if (seats <= 10) { w = 2.4; h = 1.0; }
        else { w = 3.0; h = 1.0; }
        return { width: w, height: h };
    } else if (shape === 'square') {
        let s = 1.5;
        if (seats <= 4) { s = 1.0; }
        else if (seats <= 8) { s = 1.5; }
        else { s = 1.8; }
        return { width: s, height: s };
    } else {
        // Living
        let w = 2.9; let h = 2.0;
        if (seats <= 4) { w = 2.0; h = 1.5; }
        else if (seats <= 6) { w = 2.5; h = 1.6; }
        else if (seats <= 8) { w = 2.9; h = 2.0; }
        else if (seats <= 10) { w = 3.3; h = 2.5; }
        else { w = 3.7; h = 2.9; }
        return { width: w, height: h };
    }
};

const getRectFromConfig = (item: { x: number, y: number, width: number, height: number, orientation: ItemOrientation }): Rect => {
  const isVertical = item.orientation === 'vertical';
  return {
    x: item.x,
    y: item.y,
    w: isVertical ? item.height : item.width,
    h: isVertical ? item.width : item.height
  };
};

// Detecta colisión entre dos rectángulos con un buffer (AABB)
const checkRectCollision = (r1: Rect, r2: Rect, buffer: number = 0): boolean => {
  return (
    r1.x - r1.w/2 - buffer < r2.x + r2.w/2 &&
    r1.x + r1.w/2 + buffer > r2.x - r2.w/2 &&
    r1.y - r1.h/2 - buffer < r2.y + r2.h/2 &&
    r1.y + r1.h/2 + buffer > r2.y - r2.h/2
  );
};

const checkTableCollision = (
    t1: { x: number, y: number, width: number, height: number, shape: TableShape, orientation: ItemOrientation },
    t2: { x: number, y: number, width: number, height: number, shape: TableShape, orientation: ItemOrientation },
    buffer: number
): boolean => {
    const isT1Round = t1.shape === 'round';
    const isT2Round = t2.shape === 'round';

    // Caso 1: Ambos redondos (Distancia Euclidiana) - Más preciso para packing
    if (isT1Round && isT2Round) {
        const dx = t1.x - t2.x;
        const dy = t1.y - t2.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const minDistance = (t1.width / 2) + (t2.width / 2) + buffer;
        return distance < minDistance;
    }

    // Caso 2: AABB simplificado
    const r1 = getRectFromConfig(t1);
    const r2 = getRectFromConfig(t2);
    return checkRectCollision(r1, r2, buffer);
};

const isInsideVenue = (r: Rect, venue: VenueConfig, margin: number = 0): boolean => {
  return (
    r.x - r.w/2 >= margin &&
    r.x + r.w/2 <= venue.width - margin &&
    r.y - r.h/2 >= margin &&
    r.y + r.h/2 <= venue.length - margin
  );
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<Theme>('dark');
  
  // --- ESTADOS DE LA APLICACIÓN (Sin persistencia) ---

  const [formData, setFormData] = useState<FormData>(JSON.parse(JSON.stringify(INITIAL_FORM_DATA)));

  const [venueConfig, setVenueConfig] = useState<VenueConfig>({ width: DEFAULT_VENUE_WIDTH, length: DEFAULT_VENUE_LENGTH });
  const [tables, setTables] = useState<TableConfig[]>([]);
  const [danceFloor, setDanceFloor] = useState<DanceFloorConfig>({
    enabled: false, x: 0, y: 0, width: 0, height: 0, orientation: 'horizontal'
  });
  const [customElements, setCustomElements] = useState<CustomElementConfig[]>([]);
  
  // Nuevo Estado: Indica si ya se generó el plano por primera vez
  const [hasGenerated, setHasGenerated] = useState<boolean>(false);

  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [selectedCustomElementId, setSelectedCustomElementId] = useState<string | null>(null);
  const [isDanceFloorSelected, setIsDanceFloorSelected] = useState(false);
  
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);


  // --- Cálculos de Capacidad en Tiempo Real ---
  const venueCapacityStats = useMemo(() => {
    const width = parseFloat(formData.venueWidth) || 0;
    const length = parseFloat(formData.venueLength) || 0;
    const totalArea = width * length;
    
    // Calcular área ocupada por estructuras fijas
    let occupiedArea = 0;
    if (danceFloor.enabled) occupiedArea += danceFloor.width * danceFloor.height;
    customElements.forEach(el => occupiedArea += el.width * el.height);
    if (formData.enableMainTable) {
         occupiedArea += parseFloat(formData.mainTableData.width) * parseFloat(formData.mainTableData.length);
    }

    const usableArea = Math.max(0, totalArea - occupiedArea);
    
    // Estimación de densidad de ocupación (m2 por persona)
    const getDensityFactor = (shape: TableShape): number => {
        switch(shape) {
            case 'round': return 1.1;
            case 'rectangular': return 0.9;
            case 'square': return 1.0;
            case 'living': return 1.4;
            default: return 1.1;
        }
    };
    
    const density = getDensityFactor(formData.tableShape);
    // Capacidad máxima teórica del salón basada en m2 útiles
    const maxGuests = Math.floor(usableArea / density);

    const targetGuests = parseInt(formData.guests) || 0;
    
    // Semáforo de ocupación
    const capacityRatio = maxGuests > 0 ? targetGuests / maxGuests : 1;
    let occupancyStatus = 'ok'; // ok, warning, error
    if (capacityRatio > 1) occupancyStatus = 'error';
    else if (capacityRatio > 0.85) occupancyStatus = 'warning';

    // Asientos reales generados en el plano
    const seatsPerTable = parseInt(formData.seatsPerStandardTable) || 8;
    const totalTables = tables.filter(t => !t.isMainTable && !t.isGhost).length;

    return {
      maxGuests,
      occupancyStatus,
      totalTables,
      tablesNeeded: Math.ceil(targetGuests / seatsPerTable)
    };

  }, [formData, danceFloor, customElements, tables]);


  const handleToggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, 
    field?: string
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // 1. Update Form Data
    if (field) {
      setFormData(prev => {
        const newData = { ...prev };
        if (field.startsWith('mainTableData.')) {
          const key = field.split('.')[1] as keyof MainTableFormData;
          newData.mainTableData = { ...newData.mainTableData, [key]: value };
        } else if (field.startsWith('customElements.')) {
          const parts = field.split('.');
          const index = parseInt(parts[1]);
          const key = parts[2] as keyof CustomElementFormData;
          if (newData.customElements && newData.customElements[index]) {
             newData.customElements[index] = { ...newData.customElements[index], [key]: value };
          }
        } else {
          // @ts-ignore
          newData[field] = type === 'checkbox' ? checked : value;
        }
        return newData;
      });
    } else {
       setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }

    // 2. Real-time Venue Resize (Immediate Feedback)
    if (name === 'venueWidth') {
        const val = parseFloat(value);
        if (!isNaN(val) && val > 0) setVenueConfig(prev => ({ ...prev, width: val }));
    }
    if (name === 'venueLength') {
        const val = parseFloat(value);
        if (!isNaN(val) && val > 0) setVenueConfig(prev => ({ ...prev, length: val }));
    }
  };

  const handleAddCustomElement = () => {
    setFormData(prev => ({
      ...prev,
      customElements: [
        ...prev.customElements,
        { id: Date.now().toString(), name: '', width: '', height: '' }
      ]
    }));
  };

  const handleRemoveCustomElement = (id: string) => {
    setFormData(prev => ({
      ...prev,
      customElements: prev.customElements.filter(el => el.id !== id)
    }));
  };

  // --- GENERACIÓN DEL PLANO ---
  const handleGeneratePlan = () => {
    const vWidth = parseFloat(formData.venueWidth);
    const vLength = parseFloat(formData.venueLength);
    const numGuests = parseInt(formData.guests);
    const seatsPerTable = parseInt(formData.seatsPerStandardTable);
    
    if (isNaN(vWidth) || isNaN(vLength) || vWidth <= 0 || vLength <= 0) {
      setWarnings(["Dimensiones del salón inválidas."]);
      return;
    }

    setVenueConfig({ width: vWidth, length: vLength });
    const newWarnings: string[] = [];

    const obstacles: { rect: Rect, type: string, config?: any }[] = [];

    let currentDanceFloor = { ...danceFloor };
    if (formData.danceFloorEnabled) {
        const { width: suggW, height: suggH } = getSuggestedDanceFloorDims(numGuests);
        const dfW = parseFloat(formData.danceFloorCustomWidth) || suggW;
        const dfH = parseFloat(formData.danceFloorCustomHeight) || suggH;
        
        currentDanceFloor = {
            enabled: true,
            width: dfW,
            height: dfH,
            x: currentDanceFloor.enabled ? currentDanceFloor.x : vWidth / 2, // Preserve pos if enabled, else center
            y: currentDanceFloor.enabled ? currentDanceFloor.y : vLength / 2,
            orientation: currentDanceFloor.orientation || 'horizontal'
        };
        obstacles.push({ rect: getRectFromConfig(currentDanceFloor), type: 'danceFloor' });
    } else {
        currentDanceFloor = { ...danceFloor, enabled: false };
    }
    
    setDanceFloor(currentDanceFloor);

    const newCustomElements: CustomElementConfig[] = formData.customElements.map((ce, idx) => {
        const w = parseFloat(ce.width) || DEFAULT_CUSTOM_ELEMENT_WIDTH;
        const h = parseFloat(ce.height) || DEFAULT_CUSTOM_ELEMENT_HEIGHT;
        // Distribución simple inicial si no tienen posición
        // Preserve existing position if ID matches
        const existing = customElements.find(old => old.id === ce.id);

        let x = existing ? existing.x : (idx % 2 === 0) ? 1 + w/2 : vWidth - 1 - w/2;
        let y = existing ? existing.y : 1 + h/2 + (Math.floor(idx / 2) * (h + 1));
        
        if (x < 0) x = w/2; if (x > vWidth) x = vWidth - w/2;
        if (y < 0) y = h/2; if (y > vLength) y = vLength - h/2;

        const elConfig: CustomElementConfig = {
            id: ce.id,
            name: ce.name,
            width: w,
            height: h,
            x,
            y,
            orientation: existing ? existing.orientation : 'horizontal'
        };
        obstacles.push({ rect: getRectFromConfig(elConfig), type: 'custom' });
        return elConfig;
    });
    setCustomElements(newCustomElements);

    let mainTableConfig: TableConfig | null = null;
    if (formData.enableMainTable) {
        const mtW = parseFloat(formData.mainTableData.width);
        const mtL = parseFloat(formData.mainTableData.length);
        const mtSeats = parseInt(formData.mainTableData.seats);
        
        // Preserve pos if possible
        const existingMain = tables.find(t => t.isMainTable);
        const mtX = existingMain ? existingMain.x : vWidth / 2;
        const mtY = existingMain ? existingMain.y : mtL / 2 + 1;

        mainTableConfig = {
            id: 'main-table',
            displayId: 'Principal',
            shape: 'rectangular',
            seats: mtSeats,
            assignedSeats: 0, 
            guestList: [], 
            x: mtX,
            y: mtY,
            orientation: existingMain ? existingMain.orientation : 'horizontal',
            width: mtW,
            height: mtL,
            isMainTable: true,
            isGhost: false
        };
        obstacles.push({ rect: getRectFromConfig(mainTableConfig), type: 'mainTable' });
    }

    const dims = getPhysicalDimensions(formData.tableShape, seatsPerTable);
    const neededTables = Math.ceil(numGuests / seatsPerTable);
    
    // --- GENERADOR DE MALLA CENTRADA Y ELÁSTICA (ELASTIC SYMMETRIC GRID) ---
    
    const attemptPlacement = (buffer: number): TableConfig[] => {
        const tempTables: TableConfig[] = [];
        if (mainTableConfig) tempTables.push(mainTableConfig);
        
        const isRound = formData.tableShape === 'round';
        const itemW = dims.width + buffer;
        const itemH = dims.height + buffer;
        
        // Foco de atracción: Pista o Centro
        const focusPoint = currentDanceFloor.enabled 
            ? { x: currentDanceFloor.x, y: currentDanceFloor.y }
            : { x: vWidth / 2, y: vLength / 2 };

        // FASE 1: MALLA ESTÉTICA ELÁSTICA (Elastic Grid)
        const candidates: { x: number, y: number, dist: number }[] = [];
        
        const rowHeight = isRound ? itemH * 0.866 : itemH;

        const distToCorner = Math.sqrt(Math.pow(Math.max(focusPoint.x, vWidth - focusPoint.x), 2) + Math.pow(Math.max(focusPoint.y, vLength - focusPoint.y), 2));
        const maxRows = Math.ceil(distToCorner / rowHeight) + 2; 
        const maxCols = Math.ceil(distToCorner / itemW) + 2;

        for (let row = -maxRows; row <= maxRows; row++) {
            const y = focusPoint.y + row * rowHeight; 
            const xOffset = (isRound && Math.abs(row) % 2 === 1) ? itemW / 2 : 0;

            for (let col = -maxCols; col <= maxCols; col++) {
                const x = focusPoint.x + col * itemW + xOffset; 
                
                if (x < -itemW || x > vWidth + itemW || y < -itemH || y > vLength + itemH) continue;
                
                // Ordenamiento radial desde el foco
                const dist = Math.sqrt((x - focusPoint.x)**2 + (y - focusPoint.y)**2);
                candidates.push({ x, y, dist });
            }
        }
        
        // Ordenar por distancia (llenado radial orgánico)
        candidates.sort((a, b) => a.dist - b.dist);

        let placedCount = 0;

        const placeTableIfValid = (x: number, y: number) => {
             if (placedCount >= neededTables) return false;
             const candidateTable = { 
                x: x, y: y, width: dims.width, height: dims.height, 
                shape: formData.tableShape, orientation: 'horizontal' as ItemOrientation 
            };
             const candidateRect = getRectFromConfig(candidateTable);

             if (!isInsideVenue(candidateRect, { width: vWidth, length: vLength }, 0.1)) return false;
             
             // Check against fixed obstacles
             for (const obs of obstacles) {
                if (checkRectCollision(candidateRect, obs.rect, TABLE_TO_OBSTACLE_BUFFER)) return false;
             }
             // Check against already placed tables
             for (const t of tempTables) {
                if (checkTableCollision(candidateTable, t, buffer)) return false;
             }

             const tableId = `t-${placedCount + 1}`;
             tempTables.push({
                id: tableId,
                displayId: `${placedCount + 1}`,
                shape: formData.tableShape,
                seats: seatsPerTable,
                assignedSeats: 0,
                guestList: [],
                x: x, y: y, orientation: 'horizontal', width: dims.width, height: dims.height,
                clearanceRadius: (dims.width / 2) + 0.5, 
                isMainTable: false, isGhost: false
             });
             placedCount++;
             return true;
        };

        for (const pt of candidates) {
            if (placedCount >= neededTables) break;

            if (placeTableIfValid(pt.x, pt.y)) {
                continue;
            }
            
            // ELASTIC GRID LOGIC
            const candidateRect = getRectFromConfig({ x: pt.x, y: pt.y, width: dims.width, height: dims.height, orientation: 'horizontal' });
            let hitObstacle = false;
            for (const obs of obstacles) {
                 if (checkRectCollision(candidateRect, obs.rect, TABLE_TO_OBSTACLE_BUFFER)) {
                     hitObstacle = true;
                     break;
                 }
            }

            if (hitObstacle) {
                 let dx = pt.x - focusPoint.x;
                 let dy = pt.y - focusPoint.y;
                 const mag = Math.sqrt(dx*dx + dy*dy);
                 if (mag > 0.1) {
                     const ndx = dx / mag;
                     const ndy = dy / mag;
                     
                     for (let offset = 0.1; offset <= 1.5; offset += 0.1) {
                         const nudgedX = pt.x + ndx * offset;
                         const nudgedY = pt.y + ndy * offset;
                         if (placeTableIfValid(nudgedX, nudgedY)) {
                             break; 
                         }
                     }
                 }
            }
        }

        // FASE 2: RELLENO DE HUECOS (DENSE SCAN / GAP FILLER)
        if (placedCount < neededTables) {
            const denseCandidates: { x: number, y: number, dist: number }[] = [];
            const step = 0.05; // 5cm de precisión
            
            for (let y = step; y < vLength; y += step) {
                for (let x = step; x < vWidth; x += step) {
                    const dist = (x - focusPoint.x)**2 + (y - focusPoint.y)**2;
                    denseCandidates.push({ x, y, dist });
                }
            }
            denseCandidates.sort((a, b) => a.dist - b.dist);

            for (const pt of denseCandidates) {
                if (placedCount >= neededTables) break;
                placeTableIfValid(pt.x, pt.y);
            }
        }

        return tempTables;
    };

    const buffersToTry = [AUTO_TABLE_BUFFER, 1.0, MANUAL_TABLE_BUFFER];
    let finalTables: TableConfig[] = [];
    let finalTablesPlacedCount = 0;

    for (const buffer of buffersToTry) {
        const generated = attemptPlacement(buffer);
        const placedStandard = generated.filter(t => !t.isMainTable).length;
        
        if (placedStandard >= neededTables) {
            finalTables = generated;
            finalTablesPlacedCount = placedStandard;
            break;
        }
        
        if (generated.length > finalTables.length) {
            finalTables = generated;
            finalTablesPlacedCount = placedStandard;
        }
    }

    const tablesMissing = neededTables - finalTablesPlacedCount;
    if (tablesMissing > 0) {
        const itemLabel = formData.tableShape === 'living' ? 'livings' : 'mesas';
        newWarnings.push(`Espacio insuficiente para ${tablesMissing} ${itemLabel} incluso en configuración compacta.`);
    }

    setTables(finalTables);
    setWarnings(newWarnings);
    setHasGenerated(true);
    
    if (tables.length === 0 && canvasRef.current) {
        canvasRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // --- EFECTO DE AUTO-ACTUALIZACIÓN ---
  useEffect(() => {
    if (!hasGenerated) return;
    const timer = setTimeout(() => handleGeneratePlan(), 500);
    return () => clearTimeout(timer);
  }, [
    formData.guests,
    formData.venueWidth,
    formData.venueLength,
    formData.tableShape,
    formData.seatsPerStandardTable,
    formData.enableMainTable,
    formData.mainTableData.width,
    formData.mainTableData.length,
    formData.mainTableData.seats,
    formData.customElements.length, 
    formData.danceFloorCustomWidth,
    formData.danceFloorCustomHeight
  ]);

  // MANEJO DE RESETEO ROBUSTO
  const handleResetPlan = () => {
    if (window.confirm("⚠️ ¿Estás seguro de que deseas restablecer todo?\n\nSe borrarán el plano actual y la configuración. La página se recargará.")) {
        // Forzamos un 'Hard Reload' para limpiar cualquier estado en memoria o en librerías de terceros.
        window.location.href = window.location.href;
    }
  };

  // --- MANEJO DE MOVIMIENTO (SOLVER DE FÍSICA) ---
  const resolvePosition = (
      candidateTable: TableConfig, 
      currentId: string,
      externalObstacles: { rect: Rect, buffer: number, isTable?: boolean, table?: TableConfig }[] | null = null
  ): { x: number, y: number } => {
      
      let { x, y } = candidateTable;
      const maxIterations = 3;
      
      for (let i = 0; i < maxIterations; i++) {
          let collided = false;
          const currentCandidate = { ...candidateTable, x, y };
          const r1 = getRectFromConfig(currentCandidate);

          // Clamp hard
          const halfW = r1.w / 2;
          const halfH = r1.h / 2;
          const margin = 0;
          
          if (x - halfW < margin) { x = halfW + margin; collided = true; }
          if (x + halfW > venueConfig.width - margin) { x = venueConfig.width - halfW - margin; collided = true; }
          if (y - halfH < margin) { y = halfH + margin; collided = true; }
          if (y + halfH > venueConfig.length - margin) { y = venueConfig.length - halfH - margin; collided = true; }

          let colliders = [];
          if (externalObstacles) {
              colliders = externalObstacles;
          } else {
              if (danceFloor.enabled) colliders.push({ rect: getRectFromConfig(danceFloor), buffer: TABLE_TO_OBSTACLE_BUFFER });
              customElements.forEach(ce => {
                  if (ce.id !== currentId) colliders.push({ rect: getRectFromConfig(ce), buffer: TABLE_TO_OBSTACLE_BUFFER });
              });
              tables.forEach(t => {
                  if (t.id !== currentId) colliders.push({ 
                      rect: getRectFromConfig(t), 
                      buffer: MANUAL_TABLE_BUFFER, 
                      isTable: true, 
                      table: t 
                  });
              });
          }

          for (const col of colliders) {
              const buffer = col.buffer;
              let isCollision = false;
              
              if (col.isTable && col.table) {
                  isCollision = checkTableCollision({ ...currentCandidate, x, y }, col.table, buffer);
              } else {
                  isCollision = checkRectCollision(getRectFromConfig({ ...currentCandidate, x, y }), col.rect, buffer);
              }

              if (isCollision) {
                  collided = true;
                  let dx = x - col.rect.x;
                  let dy = y - col.rect.y;
                  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) dy = 1;

                  const currentDist = Math.sqrt(dx*dx + dy*dy);
                  const minDist = (currentCandidate.width/2) + (col.rect.w/2) + buffer;

                  if (currentDist < minDist) {
                      const pushDist = minDist - currentDist + 0.01;
                      const nx = dx / currentDist;
                      const ny = dy / currentDist;
                      x += nx * pushDist;
                      y += ny * pushDist;
                  }
              }
          }
          if (!collided) break; 
      }
      return { x, y };
  };

  const handleUpdateTable = (updatedTable: TableConfig) => {
    const { x: newX, y: newY } = resolvePosition(updatedTable, updatedTable.id);
    setTables(prev => prev.map(t => t.id === updatedTable.id ? { ...updatedTable, x: newX, y: newY } : t));
  };

  const handleUpdateDanceFloor = (updatedDF: DanceFloorConfig) => {
      const dfRect = getRectFromConfig(updatedDF);
      if (!isInsideVenue(dfRect, venueConfig, 0)) return; 

      const pushOutOfDF = (item: any) => {
          const iRect = getRectFromConfig(item);
          if (!checkRectCollision(iRect, dfRect, TABLE_TO_OBSTACLE_BUFFER)) return item;

          const dx = item.x - updatedDF.x;
          const dy = item.y - updatedDF.y;
          const minHalfW = (dfRect.w + iRect.w)/2 + TABLE_TO_OBSTACLE_BUFFER;
          const minHalfH = (dfRect.h + iRect.h)/2 + TABLE_TO_OBSTACLE_BUFFER;

          const ox = minHalfW - Math.abs(dx);
          const oy = minHalfH - Math.abs(dy);

          let nx = item.x;
          let ny = item.y;

          if (ox < oy) {
              nx = dx > 0 ? updatedDF.x + minHalfW : updatedDF.x - minHalfW;
          } else {
              ny = dy > 0 ? updatedDF.y + minHalfH : updatedDF.y - minHalfH;
          }
          
          return { ...item, x: nx, y: ny };
      };

      let nextTables = tables.map(pushOutOfDF);
      let nextCustom = customElements.map(pushOutOfDF);

      const iterations = 6;
      for (let i = 0; i < iterations; i++) {
          const allItems = [
              ...nextTables.map(t => ({...t, type: 'table'})),
              ...nextCustom.map(c => ({...c, type: 'custom'}))
          ];

          const applyRepulsion = (item: any) => {
               let { x, y } = item;
               let modified = false;

               const r = getRectFromConfig(item);
               const hw = r.w/2; const hh = r.h/2;
               if(x - hw < 0) { x = hw; modified=true; }
               if(x + hw > venueConfig.width) { x = venueConfig.width - hw; modified=true; }
               if(y - hh < 0) { y = hh; modified=true; }
               if(y + hh > venueConfig.length) { y = venueConfig.length - hh; modified=true; }

               if (checkRectCollision(getRectFromConfig({...item, x, y}), dfRect, TABLE_TO_OBSTACLE_BUFFER)) {
                   const pushed = pushOutOfDF({...item, x, y});
                   x = pushed.x; y = pushed.y;
                   modified = true;
               }

               for (const other of allItems) {
                   if (other.id === item.id) continue;
                   
                   const isCollision = (item.type === 'table' && other.type === 'table')
                        ? checkTableCollision({...item, x, y}, other, MANUAL_TABLE_BUFFER)
                        : checkRectCollision(getRectFromConfig({...item, x, y}), getRectFromConfig(other), MANUAL_TABLE_BUFFER);

                   if (isCollision) {
                       let dx = x - other.x;
                       let dy = y - other.y;
                       if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) { dx = (Math.random()-0.5); dy = (Math.random()-0.5); }
                       
                       const dist = Math.sqrt(dx*dx + dy*dy);
                       const minDist = (item.width/2 + other.width/2) + MANUAL_TABLE_BUFFER;
                       
                       if (dist < minDist) {
                           const push = (minDist - dist) * 0.5; 
                           const nx = dx / dist;
                           const ny = dy / dist;
                           x += nx * push;
                           y += ny * push;
                           modified = true;
                       }
                   }
               }
               return modified ? { ...item, x, y } : item;
          };

          nextTables = nextTables.map(applyRepulsion);
          nextCustom = nextCustom.map(applyRepulsion);
      }

      setDanceFloor(updatedDF);
      setTables(nextTables);
      setCustomElements(nextCustom);
  };

  useEffect(() => {
    const vWidth = parseFloat(formData.venueWidth);
    const vLength = parseFloat(formData.venueLength);

    if (formData.danceFloorEnabled) {
        const numGuests = parseInt(formData.guests);
        const { width: suggW, height: suggH } = getSuggestedDanceFloorDims(numGuests);
        const dfW = parseFloat(formData.danceFloorCustomWidth) || suggW;
        const dfH = parseFloat(formData.danceFloorCustomHeight) || suggH;

        const dimsChanged = Math.abs(danceFloor.width - dfW) > 0.01 || Math.abs(danceFloor.height - dfH) > 0.01;
        const wasDisabled = !danceFloor.enabled;

        if (wasDisabled || dimsChanged) {
             const newX = wasDisabled ? vWidth / 2 : danceFloor.x;
             const newY = wasDisabled ? vLength / 2 : danceFloor.y;

             const newDFConfig: DanceFloorConfig = {
                 enabled: true,
                 width: dfW,
                 height: dfH,
                 x: newX,
                 y: newY,
                 orientation: danceFloor.orientation || 'horizontal'
             };
             handleUpdateDanceFloor(newDFConfig);
        }
    } else {
        if (danceFloor.enabled) {
            setDanceFloor(prev => ({ ...prev, enabled: false }));
        }
    }
  }, [
    formData.danceFloorEnabled, 
    formData.danceFloorCustomWidth, 
    formData.danceFloorCustomHeight, 
    formData.guests, 
    formData.venueWidth, 
    formData.venueLength
  ]);

  const handleUpdateCustomElement = (updatedCE: CustomElementConfig) => {
      const candidateRect = getRectFromConfig(updatedCE);
      let collision = false;
      
      if (danceFloor.enabled && checkRectCollision(candidateRect, getRectFromConfig(danceFloor), 0)) collision = true;
      for (const ce of customElements) {
          if (ce.id === updatedCE.id) continue;
          if (checkRectCollision(candidateRect, getRectFromConfig(ce), 0)) collision = true;
      }
      if (!isInsideVenue(candidateRect, venueConfig, 0)) collision = true;

      if (collision) return; 
      
      const updatedTables = tables.map(t => {
          if (checkRectCollision(getRectFromConfig(t), candidateRect, TABLE_TO_OBSTACLE_BUFFER)) {
               let dx = t.x - updatedCE.x;
               let dy = t.y - updatedCE.y;
               if (dx === 0 && dy === 0) dy = 1;
               const mag = Math.sqrt(dx*dx + dy*dy);
               const pushFactor = Math.max(updatedCE.width/2 + t.width/2 + TABLE_TO_OBSTACLE_BUFFER, updatedCE.height/2 + t.height/2 + TABLE_TO_OBSTACLE_BUFFER) * 1.05;

               const newTx = updatedCE.x + (dx/mag) * pushFactor;
               const newTy = updatedCE.y + (dy/mag) * pushFactor;

               const otherTables = tables.filter(ot => ot.id !== t.id).map(ot => ({ rect: getRectFromConfig(ot), buffer: MANUAL_TABLE_BUFFER, isTable: true, table: ot }));
               const obstacles = [...otherTables, { rect: candidateRect, buffer: TABLE_TO_OBSTACLE_BUFFER }];
               if (danceFloor.enabled) obstacles.push({ rect: getRectFromConfig(danceFloor), buffer: TABLE_TO_OBSTACLE_BUFFER });

               const resolved = resolvePosition({ ...t, x: newTx, y: newTy }, t.id, obstacles);
               return { ...t, x: resolved.x, y: resolved.y };
          }
          return t;
      });

      setCustomElements(prev => prev.map(el => el.id === updatedCE.id ? updatedCE : el));
      setTables(updatedTables);
  };

  const handleRotateTable = (id: string) => {
    setTables(prev => prev.map(t => {
        if (t.id !== id) return t;
        const newOrientation = t.orientation === 'horizontal' ? 'vertical' : 'horizontal';
        return { ...t, orientation: newOrientation };
    }));
  };

  const handleRotateDanceFloor = () => {
    setDanceFloor(prev => ({
        ...prev,
        orientation: prev.orientation === 'horizontal' ? 'vertical' : 'horizontal'
    }));
  };

  const handleRotateCustomElement = (id: string) => {
    setCustomElements(prev => prev.map(el => {
        if (el.id !== id) return el;
        return { ...el, orientation: el.orientation === 'horizontal' ? 'vertical' : 'horizontal' };
    }));
  };

  const handleOpenGuestModal = (id: string) => {
    setEditingTableId(id);
    setIsGuestModalOpen(true);
  };

  const handleUpdateGuestList = (tableId: string, newGuestList: string[]) => {
    setTables(prev => prev.map(t => {
        if (t.id !== tableId) return t;
        return { 
            ...t, 
            guestList: newGuestList, 
            assignedSeats: newGuestList.length 
        };
    }));
  };

  return (
    <div className={`min-h-screen flex flex-col font-sans transition-colors duration-300 ${theme === 'dark' ? 'bg-hba-deep-purple text-hba-text-primary' : 'bg-white text-gray-900'}`}>
      <Header theme={theme} onToggleTheme={handleToggleTheme} onShowHelp={() => setIsHelpModalOpen(true)} />
      
      {/* DASHBOARD */}
      <div className={`w-full py-4 px-6 shadow-md flex flex-col md:flex-row justify-around items-center gap-4 ${theme === 'dark' ? 'bg-hba-mid-purple/80' : 'bg-gray-100'}`}>
            
            {/* Capacidad Máxima (Left - Big) */}
            <div className="flex flex-col items-center md:items-start min-w-[150px]">
                <span className="text-xs uppercase tracking-wider opacity-70 mb-1">Capacidad Salón</span>
                <span className={`text-4xl font-bold ${parseInt(formData.guests) > venueCapacityStats.maxGuests ? 'text-red-500' : 'text-hba-accent-purple'}`}>
                    {venueCapacityStats.maxGuests}
                    <span className="text-sm ml-2 opacity-60 font-normal text-gray-400">personas máx.</span>
                </span>
            </div>

            <div className="h-10 w-px bg-gray-500/30 hidden md:block"></div>

            {/* Target Guests (Center/Right - Colored) */}
            <div className="flex flex-col items-center">
                <span className="text-xs uppercase tracking-wider opacity-70 mb-1">Tus Invitados</span>
                <div className="flex items-baseline">
                    <span className={`text-3xl font-bold ${
                        venueCapacityStats.occupancyStatus === 'ok' ? 'text-green-500' : 
                        venueCapacityStats.occupancyStatus === 'warning' ? 'text-yellow-500' : 'text-red-500'
                    }`}>
                        {formData.guests}
                    </span>
                    <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-opacity-20 bg-gray-500">
                        {venueCapacityStats.occupancyStatus === 'ok' && 'Cómodo'}
                        {venueCapacityStats.occupancyStatus === 'warning' && 'Ajustado'}
                        {venueCapacityStats.occupancyStatus === 'error' && 'Excedido'}
                    </span>
                </div>
            </div>

            <div className="h-10 w-px bg-gray-500/30 hidden md:block"></div>

            {/* Total Mesas (Replacement for Lugares Plano) */}
            <div className="flex flex-col items-center">
                <span className="text-xs uppercase tracking-wider opacity-70 mb-1">Total {formData.tableShape === 'living' ? 'Livings' : 'Mesas'}</span>
                <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-800'}`}>
                    {venueCapacityStats.totalTables}
                </span>
            </div>
            
            <div className="flex-grow md:flex-grow-0"></div>

            <div className="flex items-center">
                <button 
                    onClick={() => generatePdf(document.getElementById('floor-plan-main'), formData, tables, danceFloor, customElements)}
                    className={`py-2 px-6 rounded-full font-bold shadow-lg transition-transform transform hover:scale-105 flex items-center gap-2 ${theme === 'dark' ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                >
                    <span>📄</span> PDF
                </button>
            </div>
      </div>

      <main className="flex-grow container mx-auto p-4 flex flex-col lg:flex-row gap-6">
        
        {/* Sidebar Config */}
        <div className="w-full lg:w-1/3 xl:w-1/4 flex-shrink-0">
          <InputForm 
            formData={formData} 
            onFormChange={handleFormChange}
            onAddCustomElement={handleAddCustomElement}
            onRemoveCustomElement={handleRemoveCustomElement}
            onGeneratePlan={handleGeneratePlan}
            onResetPlan={handleResetPlan}
            theme={theme}
          />
        </div>

        {/* Main Canvas Area */}
        <div className="w-full lg:w-2/3 xl:w-3/4 flex flex-col" id="floor-plan-main">
           <div className="flex flex-col items-center w-full">
                <FloorPlanCanvas
                    venueConfig={venueConfig}
                    tables={tables}
                    danceFloor={danceFloor}
                    customElements={customElements}
                    onUpdateTable={handleUpdateTable}
                    onUpdateDanceFloor={handleUpdateDanceFloor}
                    onUpdateCustomElement={handleUpdateCustomElement}
                    onSelectTable={setSelectedTableId}
                    selectedTableId={selectedTableId}
                    onRotateTable={handleRotateTable}
                    onOpenGuestModal={handleOpenGuestModal}
                    handleRotateDanceFloor={handleRotateDanceFloor}
                    onRotateCustomElement={handleRotateCustomElement}
                    onSelectDanceFloor={() => { setIsDanceFloorSelected(true); setSelectedTableId(null); setSelectedCustomElementId(null); }}
                    isDanceFloorSelected={isDanceFloorSelected}
                    onSelectCustomElement={(id) => { setSelectedCustomElementId(id); setSelectedTableId(null); setIsDanceFloorSelected(false); }}
                    selectedCustomElementId={selectedCustomElementId}
                    theme={theme}
                    canvasRef={canvasRef}
                />
                
                <div className="mt-4 text-xs opacity-60 text-center max-w-2xl">
                    <p>Anti-colisión inteligente: Las mesas se ajustarán automáticamente si intentas superponerlas físicamente.</p>
                </div>
           </div>

           {/* --- EXTERNAL TOOLS LINKS --- */}
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 w-full max-w-4xl mx-auto">
                <a href="https://housebuenosaires.com/cotizador-hba/" target="_blank" rel="noopener noreferrer" className={`group flex flex-col items-center justify-center p-5 rounded-xl border transition-all duration-300 shadow-sm hover:shadow-md ${theme === 'dark' ? 'bg-[#2C2A4A] border-[#3E3C63] hover:border-hba-accent-pink' : 'bg-white border-gray-200 hover:border-hba-accent-purple'}`}>
                    <span className="text-3xl mb-2 group-hover:scale-110 transition-transform">🎚️</span>
                    <span className={`font-bold text-sm uppercase tracking-wide ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-800'}`}>Cotizador DJ & Técnica</span>
                    <span className={`text-xs mt-1 opacity-60 text-center ${theme === 'dark' ? 'text-hba-text-secondary' : 'text-gray-500'}`}>Presupuesto en minutos</span>
                </a>

                <a href="https://housebuenosaires.com/apps-hba/timing-planner-hba/" target="_blank" rel="noopener noreferrer" className={`group flex flex-col items-center justify-center p-5 rounded-xl border transition-all duration-300 shadow-sm hover:shadow-md ${theme === 'dark' ? 'bg-[#2C2A4A] border-[#3E3C63] hover:border-hba-accent-pink' : 'bg-white border-gray-200 hover:border-hba-accent-purple'}`}>
                    <span className="text-3xl mb-2 group-hover:scale-110 transition-transform">⏱️</span>
                    <span className={`font-bold text-sm uppercase tracking-wide ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-800'}`}>Generador de Timing</span>
                    <span className={`text-xs mt-1 opacity-60 text-center ${theme === 'dark' ? 'text-hba-text-secondary' : 'text-gray-500'}`}>Organiza el minuto a minuto</span>
                </a>

                <a href="https://housebuenosaires.com/bar-calculator-hba/" target="_blank" rel="noopener noreferrer" className={`group flex flex-col items-center justify-center p-5 rounded-xl border transition-all duration-300 shadow-sm hover:shadow-md ${theme === 'dark' ? 'bg-[#2C2A4A] border-[#3E3C63] hover:border-hba-accent-pink' : 'bg-white border-gray-200 hover:border-hba-accent-purple'}`}>
                    <span className="text-3xl mb-2 group-hover:scale-110 transition-transform">🍹</span>
                    <span className={`font-bold text-sm uppercase tracking-wide ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-800'}`}>Calculadora de Bebidas</span>
                    <span className={`text-xs mt-1 opacity-60 text-center ${theme === 'dark' ? 'text-hba-text-secondary' : 'text-gray-500'}`}>Planifica la barra ideal</span>
                </a>
           </div>

        </div>

      </main>

      <Footer theme={theme} />
      
      {/* ALERTAS FLOTANTES */}
      <AlertDisplay warnings={warnings} onClose={() => setWarnings([])} theme={theme} />
      
      <GuestListModal 
        isOpen={isGuestModalOpen} 
        onClose={() => setIsGuestModalOpen(false)} 
        table={tables.find(t => t.id === editingTableId) || null} 
        onUpdateGuestList={handleUpdateGuestList}
        theme={theme}
      />

      <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} theme={theme} />

    </div>
  );
};

export default App;
